import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box, IconButton, Loader } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { ToggleType, usePreviousState } from '@wix/rules-panel-ui';
import { RevertSmall } from '@wix/wix-ui-icons-common';
import React, { useCallback, useEffect, useMemo } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../../hooks/use-bi-logger';
import { getNumberOfIssuesInGroup, getNumberOfIssuesInVerticalGroup } from '../../../../../rule-mappers/shared/helpers';
import { useVerify } from '../../../../../rule-mappers/shared/use-verify';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { PageList } from '../lists/page-list';
import { SiteLevelTaskList } from '../lists/site-level-task-list';
const FAILURE_THRESHOLD_PERCENTAGE = 10;
export const useA11yCardWrapper = _ref => {
  let {
    cardTitle,
    pageIds,
    isCardOpen
  } = _ref;
  const {
    t
  } = useTranslation();
  const isSiteLevelCard = pageIds.includes(SITE_LEVEL_PAGE_ID);
  const {
    panelState,
    getGroupData
  } = usePanelStateAtom();
  const {
    capabilities
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const {
    verify
  } = useVerify({});
  const biLogger = useBiLogger();
  const handleSectionRescan = useCallback(async e => {
    e.stopPropagation();
    const globalComponentIds = panelState.globalSectionComponentsDataMap ? Array.from(panelState.globalSectionComponentsDataMap.values()).map(_ref2 => {
      let {
        componentId
      } = _ref2;
      return componentId;
    }) : [];
    const rescanPromises = pageIds.filter(pageId => pageId !== SITE_LEVEL_PAGE_ID).map(pageId => {
      const groupData = getGroupData(pageId);
      if (groupData && groupData.analysisState === AnalysisState.AnalyzeFailed) {
        return verify(groupData, globalComponentIds);
      }
      return null;
    }).filter(promise => promise !== null);
    await Promise.all(rescanPromises);
  }, [pageIds, getGroupData, verify, panelState.globalSectionComponentsDataMap]);
  const summary = useMemo(() => {
    let isLoading = false;
    let issueCount = 0;
    let totalPagesFailed = 0;
    let totalPagesScanned = 0;
    let pagesWithIssueCount = 0;
    for (const pageId of pageIds) {
      const group = panelState.groupsDataMap.get(pageId);
      if (!group) {
        // Treat missing group as not failed and with no issues
        continue;
      }
      const analysisState = group.analysisState;
      const {
        isPerformingAnalysis
      } = resolveGroupAnalysisState(analysisState);
      if (analysisState === AnalysisState.FirstAnalyzing || analysisState === AnalysisState.Analyzing) {
        isLoading = true;
      }
      if (!isPerformingAnalysis) {
        if (group.isTpaPage) {
          const numberOfIssues = getNumberOfIssuesInVerticalGroup(panelState, group.itemType);
          issueCount += numberOfIssues ?? 0;
          if (numberOfIssues > 0) {
            pagesWithIssueCount++;
          }
          totalPagesScanned += group.scannedPages ?? 0;
          if (analysisState === AnalysisState.AnalyzeFailed && group.failedPages === 0) {
            totalPagesFailed += group.scannedPages ?? 0;
          } else {
            totalPagesFailed += group.failedPages ?? 0;
          }
        } else {
          const numberOfIssues = getNumberOfIssuesInGroup(group.rulesStateMap);
          issueCount += numberOfIssues;
          totalPagesScanned++;
          if (numberOfIssues > 0) {
            pagesWithIssueCount++;
          }
          if (analysisState === AnalysisState.AnalyzeFailed) {
            totalPagesFailed++;
          }
        }
      }
    }
    const isFailed = totalPagesScanned > 0 && totalPagesFailed === totalPagesScanned;
    const failurePercentage = totalPagesScanned > 0 ? totalPagesFailed / totalPagesScanned * 100 : 0;
    const shouldShowRescanButton = failurePercentage > FAILURE_THRESHOLD_PERCENTAGE || issueCount === 0 && totalPagesFailed > 0;
    return {
      isFailed,
      isLoading,
      issueCount,
      pagesWithIssueCount,
      shouldShowRescanButton
    };
  }, [pageIds, panelState]);
  const {
    isFailed,
    isLoading,
    issueCount,
    shouldShowRescanButton,
    pagesWithIssueCount
  } = summary;
  const previousIsLoading = usePreviousState(isLoading);
  useEffect(() => {
    if (previousIsLoading && !isLoading) {
      biLogger.wizardCardFinishLoading({
        cardName: cardTitle,
        issueCount: shouldShowRescanButton ? -1 : issueCount
      });
    }
  }, [previousIsLoading, isLoading, biLogger, cardTitle, issueCount, shouldShowRescanButton]);
  const suffix = useMemo(() => {
    if (isLoading) {
      return /*#__PURE__*/React.createElement(Box, {
        verticalAlign: "middle"
      }, /*#__PURE__*/React.createElement(Loader, {
        size: "tiny"
      }));
    }
    if (shouldShowRescanButton) {
      return /*#__PURE__*/React.createElement(IconButton, {
        priority: compactPanelChrome ? 'tertiary' : 'secondary',
        size: "tiny",
        skin: compactPanelChrome ? 'dark' : 'standard',
        onClick: handleSectionRescan
      }, /*#__PURE__*/React.createElement(RevertSmall, null));
    }
    return null;
  }, [isLoading, shouldShowRescanButton, compactPanelChrome, handleSectionRescan]);
  const badgeText = useMemo(() => {
    if (shouldShowRescanButton) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed');
    }
    if (issueCount > 0) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label', {
        numberOfIssues: issueCount
      });
    }
    return undefined;
  }, [shouldShowRescanButton, issueCount, t]);
  const cardContent = useMemo(() => {
    if (isSiteLevelCard) {
      return /*#__PURE__*/React.createElement(SiteLevelTaskList, {
        isCardOpen: isCardOpen
      });
    }
    return /*#__PURE__*/React.createElement(PageList, {
      pageIds: pageIds
    });
  }, [isSiteLevelCard, isCardOpen, pageIds]);
  const handleCardToggle = useCallback((_id, isOpen) => {
    biLogger.wizardSectionExpandCollapse({
      cardName: cardTitle,
      isAutomatic: false,
      issueCount: isLoading ? 0 : isFailed ? 0 : issueCount,
      pagesCount: pageIds.length,
      pagesWithIssueCount,
      toggleType: isOpen ? ToggleType.Expand : ToggleType.Collapse
    });
  }, [biLogger, cardTitle, isLoading, isFailed, issueCount, pageIds, pagesWithIssueCount]);
  return {
    a11yCardProps: {
      badgeText,
      badgeTooltipContent: isFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
      disabled: isSiteLevelCard && (isFailed || isLoading),
      flatCardSurface: compactPanelChrome,
      headerPadding: compactPanelChrome ? 'small' : 'medium',
      id: cardTitle,
      isFailed: shouldShowRescanButton,
      issueCount: isLoading ? undefined : issueCount,
      onToggle: handleCardToggle,
      showStickyHeaderSideShadow: !compactPanelChrome,
      suffix,
      title: cardTitle
    },
    cardContent
  };
};
//# sourceMappingURL=use-a11y-card-wrapper.js.map